import { ROAS_STANDARD_DAYS } from '@/features/roas/constants/roas-days'
import {
  getDrBaselineAtDate,
  getCostBaseline,
  getProfitBaseline,
} from '@/lib/dr-utils'
import { formatUSD } from '@/lib/format-utils'

/** Days 30+ hidden by default in Weekly/Monthly tables */
export const HIDDEN_BY_DEFAULT_DAY_ROAS = [30, 45, 60, 90, 120, 150, 180]

/** Align with ROAS weekly dayCounts gate and server DrService.sumByWeekly */
export const WEEKLY_MIN_VALID_INSTALL_DAYS = 7

export const isWeeklyRoasValueVisible = (value) =>
  value != null && Number.isFinite(value)

export function getDefaultHiddenDayRoasColumnVisibility() {
  return Object.fromEntries(
    HIDDEN_BY_DEFAULT_DAY_ROAS.map((day) => [`day${day}Roas`, false]),
  )
}

export function getDayRoasFieldName(day) {
  return `day${day}Roas`
}

/** D0 keeps ROAS- prefix; other days show D{n} only */
export function formatRoasDayLabel(day) {
  return day === 0 ? `ROAS-D${day}` : `D${day}`
}

export function getPrevDayRoasFieldName(day, period = 'weekly') {
  return period === 'monthly' ? `prevDay${day}Roas` : `preDay${day}Roas`
}

export function getBaselinePackageKey(pidForBaseline, packageName) {
  return packageName && packageName !== 'main' && packageName !== 'total' ? `${pidForBaseline}-${packageName}` : pidForBaseline
}

export function hasDrBaselineStartDate({
  drBaselines,
  day,
  country,
  packageKey,
  dateFormatted,
  mediaSource = 'total',
}) {
  const pidBaselines = drBaselines?.[day]?.[country]?.[packageKey]
  if (!pidBaselines) return false

  if (pidBaselines[dateFormatted] !== undefined) {
    return true
  }

  return pidBaselines?.[mediaSource]?.[dateFormatted] !== undefined
}

/**
 * Collect DR / cost / profit baseline changes on a single calendar day.
 */
export function collectBaselineChangesForDate({
  dateFormatted,
  dayValue,
  prevDateValue,
  drBaselines,
  country,
  pidForBaseline,
  packageName,
  costBaselines,
  profitBaselines,
}) {
  const packageKey = getBaselinePackageKey(pidForBaseline, packageName)

  let hasDrChange = false
  ROAS_STANDARD_DAYS.forEach((day) => {
    if (hasDrBaselineStartDate({ drBaselines, day, country, packageKey, dateFormatted })) {
      hasDrChange = true
    }
  })

  const costStartDate =
    costBaselines?.[country]?.[pidForBaseline]?.[dateFormatted] !== undefined
  const profitStartDate =
    profitBaselines?.[country]?.[pidForBaseline]?.[dateFormatted] !== undefined

  if (!hasDrChange && !costStartDate && !profitStartDate) {
    return null
  }

  const changeInfo = { date: dateFormatted }

  ROAS_STANDARD_DAYS.forEach((day) => {
    const dayStartDate = hasDrBaselineStartDate({
      drBaselines,
      day,
      country,
      packageKey,
      dateFormatted,
    })
    if (!dayStartDate) return

    const baseline = getDrBaselineAtDate(
      drBaselines,
      day,
      country,
      pidForBaseline,
      dayValue,
      packageName,
    )
    const prevBaseline = getDrBaselineAtDate(
      drBaselines,
      day,
      country,
      pidForBaseline,
      prevDateValue,
      packageName,
    )
    changeInfo[`day${day}`] = { from: prevBaseline, to: baseline }
  })

  if (costStartDate) {
    const costBaseline = getCostBaseline(
      costBaselines,
      country,
      pidForBaseline,
      dayValue,
    )
    const prevCostBaseline = getCostBaseline(
      costBaselines,
      country,
      pidForBaseline,
      prevDateValue,
    )
    changeInfo.cost = {
      from: formatUSD(prevCostBaseline, 0, 0),
      to: formatUSD(costBaseline, 0, 0),
    }
  }

  if (profitStartDate) {
    const profitBaseline = getProfitBaseline(
      profitBaselines,
      country,
      pidForBaseline,
      dayValue,
    )
    const prevProfitBaseline = getProfitBaseline(
      profitBaselines,
      country,
      pidForBaseline,
      prevDateValue,
    )
    changeInfo.profit = {
      from: formatUSD(prevProfitBaseline, 0, 0),
      to: formatUSD(profitBaseline, 0, 0),
    }
  }

  return changeInfo
}
